import { useEffect, useMemo, useState } from 'react';
import type { TraceStep } from '../../crdt/agent';
import type { StepRecord } from '../../state/agentTrace';
import './StepInspector.css';

/**
 * Everything observable about one step the assistant took (docs/agent.md §4).
 *
 * Four layers, in the order they actually happened, because the interesting
 * failures live in the gaps between them: the model asked for one thing, the
 * tool turned it into a different query, the archive answered, and then the
 * model was handed a clipped version of that answer.
 *
 * `detail` is the session-only record; it is absent after a reload, and for
 * collaborators who did not invoke the run. The compact `step` always exists,
 * so the request and the curl are always available.
 */
export interface StepInspectorProps {
  step: TraceStep;
  detail?: StepRecord | null;
  onClose: () => void;
}

/** A semantic-search call: "POST /api/search/shots" rather than a pandora action. */
const HTTP_ACTION = /^(GET|POST) (\/\S*)$/;

/**
 * The equivalent request as curl. `--data-urlencode`, never `-d`: an `&` inside
 * a search term would silently truncate the payload and quietly reproduce a
 * different query than the one being debugged.
 *
 * Two request shapes share the trace: pan.do/ra's form-encoded `action`+`data`
 * to `{site}/api/`, and the embeddings service's JSON to `{site}{path}`
 * (agent/tools/embeddingsPort.ts). The action string tells them apart.
 */
export function curlFor(site: string, action: string, data: unknown): string {
  const http = HTTP_ACTION.exec(action);
  if (http) {
    const [, method, path] = http;
    if (method === 'GET') {
      const params = new URLSearchParams(
        Object.entries((data ?? {}) as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
      ).toString();
      return `curl -s ${shellQuote(`${site}${path}${params ? `?${params}` : ''}`)}`;
    }
    return [
      `curl -s -X POST ${shellQuote(`${site}${path}`)} \\`,
      `  -H 'Content-Type: application/json' \\`,
      `  --data-binary ${shellQuote(JSON.stringify(data ?? {}))}`,
    ].join('\n');
  }
  const payload = JSON.stringify(data ?? {});
  return [
    `curl -s ${shellQuote(`${site}/api/`)} \\`,
    `  --data-urlencode ${shellQuote(`action=${action}`)} \\`,
    `  --data-urlencode ${shellQuote(`data=${payload}`)}`,
  ].join('\n');
}

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="ghost step-copy"
      onClick={() => {
        // Best effort: the text stays selectable, so a blocked clipboard (no
        // permission, insecure context) costs nothing.
        try {
          void navigator.clipboard?.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

function Pane({
  title,
  body,
  note,
  copy,
}: {
  title: string;
  body?: string;
  note?: string;
  copy?: string;
}) {
  return (
    <section className="step-pane">
      <h3>
        {title}
        {copy ? <CopyButton text={copy} label="Copy" /> : null}
      </h3>
      {body ? <pre className="step-pre">{body}</pre> : <p className="step-note">{note}</p>}
    </section>
  );
}

export default function StepInspector({ step, detail, onClose }: StepInspectorProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const calls = useMemo(() => step.calls ?? [], [step.calls]);

  return (
    <div className="step-overlay" onClick={onClose}>
      <div
        className="step-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Step detail: ${step.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="step-head">
          <div>
            <h2>{step.label}</h2>
            <p className="step-sub">
              <code>{step.tool}</code>
              {step.ms !== undefined ? ` · ${step.ms} ms` : ''}
              {step.state === 'error' ? ' · failed' : ''}
            </p>
          </div>
          <button className="ghost icon" onClick={onClose} title="Close">
            ×
          </button>
        </div>

        <div className="step-body">
          {step.error && <p className="step-error">{step.error}</p>}

          <Pane
            title="What the assistant asked for"
            body={step.args ? pretty(step.args) : undefined}
            note="This step took no arguments."
          />

          {detail?.rawArgs && !isSameJson(detail.rawArgs, step.args) && (
            <Pane
              title="…as the model actually sent it"
              body={detail.rawArgs}
              note=""
            />
          )}

          {calls.length === 0 ? (
            <Pane
              title="Archive requests"
              note="This step made no request to the archive — it read the timeline or the bin."
            />
          ) : (
            calls.map((call, i) => (
              <section className="step-call" key={i}>
                <h3>
                  Request to {call.site}
                  <CopyButton text={curlFor(call.site, call.action, call.data)} label="Copy curl" />
                </h3>
                <p className="step-sub">
                  <code>{call.action}</code>
                  {call.cached
                    ? ' · served from cache, no request made'
                    : ` · ${call.ms} ms${call.bytes !== undefined ? ` · ${formatBytes(call.bytes)}` : ''}`}
                  {call.httpStatus !== undefined ? ` · HTTP ${call.httpStatus}` : ''}
                  {call.apiStatus !== undefined && call.apiStatus !== 200
                    ? ` · archive status ${call.apiStatus}`
                    : ''}
                </p>
                {call.error && <p className="step-error">{call.error}</p>}
                <pre className="step-pre">{pretty(call.data)}</pre>
                <pre className="step-pre step-curl">
                  {curlFor(call.site, call.action, call.data)}
                </pre>
                <RawResponse text={detail?.calls?.[i]?.responseText} />
              </section>
            ))
          )}

          {step.opArgs && (
            <Pane
              title="Resolved into this edit operation"
              body={pretty(step.opArgs)}
              note=""
            />
          )}

          <Pane
            title="What the model was given back"
            body={detail?.content}
            note={NOT_CAPTURED}
          />
        </div>
      </div>
    </div>
  );
}

const NOT_CAPTURED =
  'Not captured in this browser session. Responses are kept in memory only, ' +
  'so they are gone after a reload and are never available to other collaborators. ' +
  'The request above is enough to reproduce it.';

function RawResponse({ text }: { text?: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return <p className="step-note">{NOT_CAPTURED}</p>;
  return (
    <>
      <button className="step-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} the raw response ({formatBytes(text.length)})
      </button>
      {open && <pre className="step-pre step-raw">{text}</pre>}
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Whether the raw string is just the parsed args again, so we can skip it. */
function isSameJson(raw: string, parsed: unknown): boolean {
  try {
    return JSON.stringify(JSON.parse(raw)) === JSON.stringify(parsed ?? {});
  } catch {
    return false;
  }
}
